import { engineComponent } from "neo-common";
import { Payline } from "../GameComponents/Payline";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Symbol } from "../GameComponents/Symbol";
import { PaylinesDefinitions } from "../GameComponents/PaylinesDefinitions";
import { CustomUtils } from "../Utils/CustomUtils";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { GameManager } from "../Managers/GameManager";
import { Communication } from "neo-games";
import { PlayList } from "../Components/Sounds/PlayList";
import { Symbols } from "../GameComponents/Symbols";
import { Paylines } from "../GameComponents/Paylines";
import { BaseView } from "../GameComponents/Base/BaseView";
import { CreateFrameTimer, FrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly StickyAnimationFinished: string = "eventStickyAnimationFinished";

    public static readonly FinishQuickWinCalculation: string = "eventFinishQuickWinCalculation";

    //******************************************************************************************************************
    //  TIMER STATUS
    //******************************************************************************************************************

    public static readonly StickyAnimationTimer: string = "stickyAnimationTimer";

    public static readonly messageTimer: string = "messageTimer";

    public static readonly MultiplierAnimationTimer: string = "multiplierAnimationTimer";

    //******************************************************************************************************************
    //  SETTINGS
    //******************************************************************************************************************

    public static readonly StickyAnimationDuration: number = 45; //in frames

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private showFirstWinsDelay: FrameTimerEventEmitter;

    private stickyContainer: ContainerElement;

    private currentStickySymbols: Array<Array<Symbol>>;

    private multiplierContainer: SwitchElement;
    private topMultipliersContainer: SwitchElement;
    private topMultiplier3x: SwitchElement;
    private topMultiplier4x: SwitchElement;
    private topMultiplier5x: SwitchElement;
    private _background: SwitchElement;
    private _transition: SwitchElement;
    private _paylines: Paylines;
    private _paylinesFields: Paylines;

    protected isSingleCashWin: boolean;
    protected hasFeatureWin: boolean;
    protected _bonusCounter: number = 0;
    protected _symbolIdleCounter: Array<Array<number>> = [
        [0, 0, 0],
        [0, 0, 0],
        [0, 0, 0],
        [0, 0, 0],
        [0, 0, 0],
    ];

    private fullReels: Array<number> = [0, 0, 0, 0, 0];
    private multiplierTeasingIndex: number = -1;
    private multiplierReelIndex: number = -1;

    protected startTimer(status: string, duration: number): void {
        this.stopTimer();
        super.startTimer(status, duration);
    }

    onReelStopped(
        reelsData: Array<Communication.Game.IReel>,
        reelIndex: number,
        reelsTransition?: SwitchElement,
        force: boolean = false,
    ): void {
        super.onReelStopped(reelsData, reelIndex, reelsTransition);

        let bonusCounterActualReel = 0;
        if (reelsData[reelIndex] != null && reelsData[reelIndex].symbols != null) {
            bonusCounterActualReel = this.checkBonusOnReel(reelsData, reelIndex);

            for (let i: number = 0; i < reelsData[reelIndex].symbols.length; i++) {
                let symbolId = reelsData[reelIndex].symbols[i];
                if (BaseView.isRespin && symbolId - 1 !== BaseView.stickySymbolId) {
                    this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseNowin);
                }
                if (CustomUtils.isBonusSymbol(symbolId) && this.currentSymbols[reelIndex][i] != null) {
                    if (
                        reelIndex <= 2 ||
                        (reelIndex === 3 && this._bonusCounter + bonusCounterActualReel >= 3) ||
                        (reelIndex === 4 && this._bonusCounter + bonusCounterActualReel >= 6)
                    ) {
                        this.currentSymbols[reelIndex][i].goToCase("IMPACT");

                        if (force === false) {
                            PlayList.setPan("SND_BaseBonusHit" + reelIndex, this.reelCount, reelIndex);
                            PlayList.play("SND_BaseBonusHit" + reelIndex, 0, false);
                        } else {
                            PlayList.play("SND_BaseBonusHit2", 0, false);
                        }
                    }
                }
            }
            this._bonusCounter += bonusCounterActualReel;
        }
        this.onSameSymbolsOnReelTeasingStop(reelIndex);
    }

    protected checkBonusOnReel(reelsData: Array<Communication.Game.IReel>, reelIndex: number): number {
        let bonusCounter = 0;
        for (let i: number = 0; i < reelsData[reelIndex].symbols.length; i++) {
            let symbolId = reelsData[reelIndex].symbols[i];
            if (CustomUtils.isBonusSymbol(symbolId) && this.currentSymbols[reelIndex][i] != null) {
                bonusCounter++;
            }
        }

        return bonusCounter;
    }

    /**
     *
     * @param reelsData
     * @param reelIndex
     */
    public multiplierGoToCase(multiplier: SwitchElement, multiplierCase: string) {
        if (multiplier != null && multiplier.getCurrentCase() !== multiplierCase) {
            multiplier.goToCase(multiplierCase);
        }
    }

    public onSameSymbolsOnReel() {
        if (this.topMultipliersContainer != null) {
            if (this.fullReels[0] >= 3 && this.fullReels[1] >= 3 && this.fullReels[2] >= 3) {
                if (this.fullReels[3] >= 3) {
                    if (this.fullReels[4] >= 3) {
                        this.multiplierGoToCase(this.topMultiplier5x, "ACTIVE");
                        this.multiplierGoToCase(this.topMultiplier4x, "INACTIVE");
                        this.multiplierGoToCase(this.topMultiplier3x, "INACTIVE");
                    } else {
                        this.multiplierGoToCase(this.topMultiplier4x, "ACTIVE");
                        this.multiplierGoToCase(this.topMultiplier3x, "INACTIVE");
                    }
                } else {
                    this.multiplierGoToCase(this.topMultiplier3x, "ACTIVE");
                }
            }
        }
    }

    public onSameSymbolsOnReelTeasingStart(multiplier: number, reelIndex: number) {
        if (this.topMultipliersContainer != null) {
            if (multiplier > this.multiplierTeasingIndex) {
                if (multiplier === 5) {
                    this.multiplierGoToCase(this.topMultiplier5x, "BOUNCING");
                } else if (multiplier === 4) {
                    this.multiplierGoToCase(this.topMultiplier4x, "BOUNCING");
                } else if (multiplier === 3) {
                    this.multiplierGoToCase(this.topMultiplier3x, "BOUNCING");
                }
            }
            this.multiplierTeasingIndex = multiplier;
            this.multiplierReelIndex = reelIndex;
        }
    }

    public onSameSymbolsOnReelTeasingStop(reelIndex: number) {
        if (this.topMultipliersContainer != null) {
            if (this.multiplierTeasingIndex > -1 && reelIndex === this.multiplierReelIndex) {
                if (this.multiplierTeasingIndex === 5 && this.topMultiplier5x.getCurrentCase() === "BOUNCING") {
                    this.multiplierGoToCase(this.topMultiplier5x, "INACTIVE");
                } else if (this.multiplierTeasingIndex === 4 && this.topMultiplier4x.getCurrentCase() === "BOUNCING") {
                    this.multiplierGoToCase(this.topMultiplier4x, "INACTIVE");
                } else if (this.multiplierTeasingIndex === 3 && this.topMultiplier3x.getCurrentCase() === "BOUNCING") {
                    this.multiplierGoToCase(this.topMultiplier3x, "INACTIVE");
                }
                this.multiplierTeasingIndex = -1;
            }
        }
    }

    //******************************************************************************************************************
    //  INITIALIZE
    //******************************************************************************************************************

    // Initialize container in BaseView.ts InitReceived()

    public initialize(
        symbols: Symbols,
        paylines?: Paylines,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
        background?: SwitchElement,
        paylinesFields?: Paylines,
    ): void {
        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);

        this._background = background;
        this._paylines = paylines;
        this._paylinesFields = paylinesFields;

        if (this._paylines != null) {
            this._paylines.reset();
        }
        if (this._paylinesFields != null) {
            this._paylinesFields.reset();
        }

        this.showFirstWinsDelay = CreateFrameTimer("showFirstWinsDelayTimer");
        this.showFirstWinsDelay.on(FrameTimerEvents.Complete, this.showFirstWins.bind(this));
    }

    public initializeStickyContainer(stickyContainer: ContainerElement): void {
        this.stickyContainer = stickyContainer;

        this.currentStickySymbols = [];

        for (let i = 0; i < this.reelCount; i++) {
            this.currentStickySymbols.push([]);

            for (let j = 0; j < this.symbolCount; j++) {
                this.currentStickySymbols[i].push(null);
            }
        }
    }

    public initializeMultiplierContainer(
        multiplierContainer: SwitchElement,
        topMultipliersContainer: SwitchElement,
    ): void {
        this.multiplierContainer = multiplierContainer;
        this.topMultipliersContainer = topMultipliersContainer;
        this.topMultiplier3x = <SwitchElement>this.topMultipliersContainer.getChildElementByName(`X3`);
        this.topMultiplier4x = <SwitchElement>this.topMultipliersContainer.getChildElementByName(`X4`);
        this.topMultiplier5x = <SwitchElement>this.topMultipliersContainer.getChildElementByName(`X5`);
    }

    public resetMultiplierContainer() {
        this.multiplierGoToCase(this.topMultiplier3x, "INACTIVE");
        this.multiplierGoToCase(this.topMultiplier4x, "INACTIVE");
        this.multiplierGoToCase(this.topMultiplier5x, "INACTIVE");
        this.multiplierTeasingIndex = -1;
        this.multiplierReelIndex = -1;
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    protected showCustomWin(): boolean {
        if (GameManager.baseResponse != null && GameManager.baseResponse.gameMultiplier > 2) {
            if (this.multiplierContainer != null) {
                // If there is a multiplier 3x or 4x or 5x, show it's GFX Container
                this.multiplierContainer.goToCase("MULTIPLIER_" + GameManager.baseResponse.gameMultiplier);
            }
            if (this.currentMultiplierIndex === 1) {
                this.currentMultiplierIndex = 2;
            }

            this.stopTimer();
            this.showFirstWinsDelay.start(CustomUtils.MultiplierAnimationDuration - 60);
            // this.startTimer(WinAnimatorExtended.MultiplierAnimationTimer, CustomUtils.MultiplierAnimationDuration - 60);
            return true;
        } else if (this.currentWins.length == 1 && this.currentWins[0].type == Communication.Game.WinType.Feature) {
            PlayList.play("SND_BaseBonusShake");
            this._transition.goToCase("BONUS_WIN");
            this.stopTimer();
            this.showFirstWinsDelay.start(CustomUtils.MultiplierAnimationDuration);
            // this.startTimer(WinAnimatorExtended.MultiplierAnimationTimer, CustomUtils.MultiplierAnimationDuration);
            return true;
        }

        return false;
    }

    protected showFirstWins(): void {
        this.checkCurrentWins();

        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        if (this._background) {
            this._background.goToCase("EMPTY");
        }

        PlayList.stop("SND_BaseRespinLoop");
        PlayList.resetVolume("SND_BaseLoop");
        PlayList.loop("SND_BaseLoop");

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.skipWin(i)) {
                //bonus symbols set to normal case, instead of win or nowin
                if (this.currentWins[i].type == Communication.Game.WinType.Feature) {
                    this.winSymbolsGoToCase(i, Symbol.CaseBonus, false);

                    this.updateActiveBonusCounters(this.currentSymbols);
                }

                continue;
            }

            this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
            this.winPaylineGoToCase(i, Payline.CaseWin);
        }

        //just feature win (no cash wins)
        if (this.currentWins.length == 1 && this.hasFeatureWin) {
            PlayList.play("SND_BaseBonusWin");
            this.stopTimer();
            this.startTimer(WinAnimatorExtended.messageTimer, 70);
            return;
        }

        this.startAnimation();
    }

    protected finishFirstWins(): void {
        this.emit(WinAnimator.FinishWinAnimator);
        this.currentCycle = 0;

        if (!this.hasFeatureWin && (CustomUtils.isRespinLast() || !CustomUtils.isRespin())) {
            this.cycleWins();
        }
    }

    public showWins(wins: Array<Communication.Game.IWin>, win: number, transition?: SwitchElement): boolean {
        this._transition = transition;
        let result = super.showWins(wins, win);
        return result;
    }

    // public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
    //     if (wins === null || wins.length === 0) {
    //         this.trezorIdle();
    //     } else {
    //         // if (!CustomUtils.isRespin()) {
    //         //     this.returnStickySymbols();
    //         //     this.allSymbolsGoToCase('NORMAL', true);
    //         // }
    //     }
    //
    //     let result = super.showWins(wins, win);
    //
    //     return result;
    // }

    // protected trezorIdle(): void {
    //     for (let i = 0; i < this.currentSymbols.length; i++) {
    //         for (let j = this.currentSymbols[i].length - 1; j >= 0; j--) {
    //             const symbol = this.currentSymbols[i][j];
    //
    //             if (symbol !== null && this.allowSymbolGoToCase(i, j, Symbol.CaseIdle) && symbol.id >= (CustomUtils.SymbolBonus-1)) {
    //                 symbol.goToCase(Symbol.CaseIdle);
    //             }
    //         }
    //     }
    // }

    //******************************************************************************************************************
    //  SHOW INIT WINS
    //******************************************************************************************************************
    // public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
    //     super.showInitWins(wins, win);
    //
    //     if (wins === null || wins.length === 0) {
    //         this.trezorIdle();
    //     }
    // }

    protected showFirstInitWins(): void {
        this.checkCurrentWins();

        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.skipWin(i)) {
                //bonus symbols set to normal case, instead of win or nowin
                if (this.currentWins[i].type == Communication.Game.WinType.Feature) {
                    this.winSymbolsGoToCase(i, Symbol.CaseBonus, false);

                    this.updateActiveBonusCounters(this.currentSymbols);
                }

                continue;
            }

            this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
            this.winPaylineGoToCase(i, Payline.CaseWin);
        }
    }

    public updateActiveBonusCounters(symbols: Array<Array<Symbol>>): void {
        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < symbols[i].length; j++) {
                let symbol = symbols[i][j];
                if (symbol && CustomUtils.isBonusSymbol(symbols[i][j].id + 1)) {
                    const topCounterIndex = symbols[i][j].id + 1 - CustomUtils.SymbolBonus - 1;
                    if (topCounterIndex != 3 && BaseView.BonusCounters != null) {
                        //if not grand (it only tease)
                        BaseView.BonusCounters.counterGoToCase(topCounterIndex, "POP");
                    }
                }
            }
        }
    }

    protected finishFirstInitWins(): void {
        this.currentCycle = 0;

        if (!this.hasFeatureWin) {
            this.cycleWins();
        }
    }

    protected allPaylinesGoToCase(caseType: string): void {
        if (this.paylines != null) {
            this.paylines.goToCase(caseType);
        }
        if (this._paylinesFields != null) {
            this._paylinesFields.goToCase(caseType);
        }
    }

    protected winPaylineGoToCase(winIndex: number, caseType: string): void {
        if (this.paylines != null && this._paylinesFields != null) {
            const win = this.currentWins[winIndex];

            if (win.trigger === Communication.Game.WinTrigger.Payline) {
                if (caseType === Payline.CaseCycle && this.currentWins.length === 1) {
                    this.paylines.goToCase(Payline.CaseSingleCycle, win.paylineId);
                    this._paylinesFields.goToCase(Payline.CaseSingleCycle, win.paylineId);
                } else {
                    this.paylines.goToCase(caseType, win.paylineId);
                    this._paylinesFields.goToCase(caseType, win.paylineId);
                }
            }
        }
    }

    //******************************************************************************************************************
    //  CYCLE WINS
    //******************************************************************************************************************

    protected cycleWins(): void {
        if (this.currentWins == null) {
            return;
        }

        //skip wins bonus ...
        if (this.skipWin(this.currentCycle)) {
            this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;
            this.cycleWins();

            return;
        }

        this.allPaylinesGoToCase(Payline.CaseNowin);
        PlayList.play("SND_BaseWinline");

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
            this.winPaylineGoToCase(i, Payline.CaseNormal);
        }

        this.winSymbolsGoToCase(this.currentCycle, Symbol.CaseCycle, true);
        if (this.isSingleCashWin) {
            this.winPaylineGoToCase(this.currentCycle, Payline.CaseSingleCycle);
            return;
        }
        this.winPaylineGoToCase(this.currentCycle, Payline.CaseCycle);

        this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;

        this.startTimer(WinAnimator.CycleWins, this.getCycleWinsDelay());
    }

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected onTimerComplete(): void {
        super.onTimerComplete();
        // EXTENDED - when animation timer finish, start function
        switch (this.timerStatus) {
            case WinAnimatorExtended.StickyAnimationTimer:
                this.addStickySymbolsFinished();
                break;
            case WinAnimatorExtended.messageTimer:
                this.finishFirstWins();
                break;
            case WinAnimatorExtended.MultiplierAnimationTimer:
                this.showFirstWins();
                break;
        }
    }

    //************************************************s******************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean, spin: boolean = false): boolean {
        this.stopTimer();
        this._bonusCounter = 0;

        if (!CustomUtils.isRespin()) {
            this.returnStickySymbols();
        }

        if (showLastSymbols) {
            this.allSymbolsGoToReset();
        } else {
            this.allSymbolsGoToCase(Symbol.CaseEmpty, false);
        }

        this.allPaylinesGoToCase(Payline.CaseNormal);

        if (showLastSymbols && !spin) {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
            // this.trezorIdle();
        }

        this.value.visible = false;

        let lastMultiplierIndex = this.currentMultiplierIndex;
        this.currentMultiplierIndex = -1;

        if (this.currentAnimationIndex != null) {
            if (this.currentAnimationFinished) {
                this.resetAnimations();

                this.currentWins = null;
                this.currentWin = null;
                return false;
            } else {
                this.stopAnimation();

                this.currentWins = null;
                this.currentWin = null;
                return lastMultiplierIndex >= 3;
            }
        } else {
            this.currentWins = null;
            this.currentWin = null;
            return lastMultiplierIndex >= 3;
        }
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    // If there is already StickedSymbol, we will not add CurrentSymbol to WinAnimator
    protected addCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        super.addCurrentSymbol(reelIndex, symbolIndex);

        let symbol = this.currentSymbols[reelIndex][symbolIndex];
        if (this.currentReels[reelIndex].customData != null) {
            if (
                this.currentReels[reelIndex].customData[symbolIndex] != null &&
                this.currentReels[reelIndex].customData[symbolIndex] > 0
            ) {
                let amount = this.currentReels[reelIndex].customData[symbolIndex];

                if (amount > 0) {
                    symbol.setAmount(amount);
                }
            }
        }
    }

    protected allSymbolsGoToReset(): void {
        let caseType = Symbol.CaseNormal;

        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = this.currentSymbols[i].length - 1; j >= 0; j--) {
                let symbol = this.currentSymbols[i][j];

                if (symbol !== null && this.allowSymbolGoToCase(i, j, caseType)) {
                    const currentCaseType = symbol.getCurrentCase();

                    if (
                        caseType !== currentCaseType &&
                        currentCaseType.indexOf(Symbol.CaseIdle) === -1 &&
                        currentCaseType.indexOf(Symbol.CaseIdle0) === -1 &&
                        currentCaseType.indexOf(Symbol.CaseIdle1) === -1 &&
                        currentCaseType.indexOf(Symbol.CaseIdle2) === -1 &&
                        currentCaseType.indexOf(Symbol.CaseIdle3) === -1
                    ) {
                        symbol.goToCase(caseType);
                    }

                    this.symbolsContainer.addChild(symbol);
                }

                symbol = this.currentStickySymbols[i][j];
                if (symbol !== null && this.allowSymbolGoToCase(i, j, caseType)) {
                    let currentCaseType = symbol.getCurrentCase();
                    let caseTypeSticky = "STICKY_" + caseType;

                    if (
                        caseTypeSticky !== currentCaseType &&
                        currentCaseType.indexOf(Symbol.CaseIdle) === -1 &&
                        currentCaseType.indexOf(Symbol.CaseIdle0) === -1 &&
                        currentCaseType.indexOf(Symbol.CaseIdle1) === -1 &&
                        currentCaseType.indexOf(Symbol.CaseIdle2) === -1 &&
                        currentCaseType.indexOf(Symbol.CaseIdle3) === -1
                    ) {
                        symbol.goToCase(caseTypeSticky);
                    }

                    this.stickyContainer.addChild(symbol);
                }
            }
        }
    }

    protected allSymbolsGoToCase(caseType: string, noWin: boolean): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                let symbol = this.currentSymbols[i][j];
                let currentCaseType = caseType;
                if (symbol !== null && this.allowSymbolGoToCase(i, j, caseType)) {
                    symbol.goToCase(currentCaseType);
                    if (noWin) {
                        this.noWinSymbolsContainer.addChild(symbol);
                    } else {
                        this.symbolsContainer.addChild(symbol);
                    }
                }

                symbol = this.currentStickySymbols[i][j];
                if (symbol !== null && this.allowSymbolGoToCase(i, j, caseType)) {
                    currentCaseType = "STICKY_" + caseType;
                    symbol.goToCase(currentCaseType);
                    if (noWin) {
                        this.noWinSymbolsContainer.addChild(symbol);
                    } else {
                        this.stickyContainer.addChild(symbol);
                    }
                }
            }
        }
    }

    protected winSymbolsGoToCaseAsPayline(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        if (this.paylines != null) {
            const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                win.paylineId,
                this.paylines.paylineDefinition,
            );

            for (let i = 0; i < definition.length; i++) {
                if (win.mask[i] === 1 && this.allowSymbolGoToCase(i, definition[i], caseType)) {
                    let symbol = this.currentStickySymbols[i][definition[i]];
                    let currentCaseType = caseType;

                    if (symbol == null) {
                        symbol = this.currentSymbols[i][definition[i]];
                    } else {
                        currentCaseType = "STICKY_" + caseType;
                    }

                    symbol.goToCase(currentCaseType);
                    this.symbolsContainer.addChild(symbol);
                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    //******************************************************************************************************************
    //  ANIMATIONS
    //******************************************************************************************************************

    protected createAnimations(): void {
        super.createAnimations();

        for (let i = 0; i < this.animationNames.length; i++) {
            if (this.childs[this.animationNames[i]]) {
                let animation = this.childs[this.animationNames[i]];
                animation.on("calculationFinished", this.onCalculationFinished.bind(this));
            }
        }
    }

    protected onCalculationFinished(): void {
        //win0 has no counting duration, so we want to override total win update
        this.emit(WinAnimatorExtended.FinishQuickWinCalculation);
    }

    protected stopAnimation(): void {
        super.stopAnimation();
    }

    protected onAnimationFinished(): void {
        super.onAnimationFinished();
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************

    protected idle(): void {
        for (let i = 0; i < this.getIdleActiveElementsCount(); i++) {
            let indexI = Math.floor(Math.random() * this.reelCount);
            let indexJ = Math.floor(Math.random() * this.symbolCount);

            const symbol = this.currentSymbols[indexI][indexJ];

            if (symbol != null && symbol.getCurrentCase() === Symbol.CaseNormal) {
                if (symbol.hasCase(Symbol.CaseIdle + "_" + this._symbolIdleCounter[indexI][indexJ])) {
                    symbol.goToCase(Symbol.CaseIdle + "_" + this._symbolIdleCounter[indexI][indexJ]);
                    this._symbolIdleCounter[indexI][indexJ]++;
                } else {
                    this._symbolIdleCounter[indexI][indexJ] = 0;
                    if (symbol.hasCase(Symbol.CaseIdle + "_" + this._symbolIdleCounter[indexI][indexJ])) {
                        symbol.goToCase(Symbol.CaseIdle + "_" + this._symbolIdleCounter[indexI][indexJ]);
                        this._symbolIdleCounter[indexI][indexJ]++;
                    }
                }
            }
        }

        this.startTimer(WinAnimator.Idle, this.getIdleDelay());
    }

    protected getIdleActiveElementsCount(): number {
        return 3;
    }

    protected getInitialIdleDelay(): number {
        return 120;
    }

    protected getIdleDelay(): number {
        return 120;
    }

    //******************************************************************************************************************
    //  ADDITIONAL
    //******************************************************************************************************************

    protected checkCurrentWins(): void {
        this.isSingleCashWin = false;
        this.hasFeatureWin = false;
        let wins = 0;

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].type == Communication.Game.WinType.Cash) {
                wins++;
            }

            if (this.currentWins[i].type == Communication.Game.WinType.Feature) {
                this.hasFeatureWin = true;
            }
        }

        this.isSingleCashWin = wins > 0 && wins == 1;
    }

    protected skipWin(currentWinIndex: number): boolean {
        if (this.currentWins != null && this.currentWins[currentWinIndex] != null) {
            let win = this.currentWins[currentWinIndex];

            if (win.type == Communication.Game.WinType.Feature) {
                return true;
            }
        }

        return false;
    }

    //******************************************************************************************************************
    //  STICKY
    //******************************************************************************************************************

    public hasStickySymbol(reelIndex: number): boolean {
        // On OnReelsStopped  we go trough respons to find if there is some "transform" on reels - StickySymbol
        if (this.currentReels[reelIndex].transform != null) {
            return true;
        }

        return false;
    }

    public hasSticky(): boolean {
        for (let i = 0; i < this.reelCount; i++) {
            if (this.hasStickySymbol(i)) {
                return true;
            }
        }

        return false;
    }

    public showStickySymbols(): void {
        for (let i = 0; i < this.reelCount; i++) {
            // On OnReelsStopped  we go trough respons to find if there is some "overlay" on reels - StickySymbol for Init
            if (this.currentReels[i].overlay != null) {
                for (let j = 0; j < this.symbolCount; j++) {
                    // find index where is StickySymbol and call function to add StickySymbol
                    if (this.currentReels[i].overlay[j] != -1) {
                        this.addStickySymbol(i, j, this.currentReels[i].overlay[j], true);
                    }
                }
            }
        }
    }

    public addStickySymbols(reelIndex: number): void {
        if (this.currentReels[reelIndex].transform != null) {
            for (let j = 0; j < this.symbolCount; j++) {
                // find index where is StickySymbol and call function to add StickySymbol
                if (this.currentReels[reelIndex].transform[j] != -1) {
                    this.addStickySymbol(reelIndex, j, this.currentReels[reelIndex].transform[j]);
                }
            }
        }
    }

    public getReelCount() {
        return this.reelCount;
    }

    public startStickyDelay(): void {
        // Stop timers in case some is running AND start TIMER Sticky Animation
        this.stopTimer();
        this.startTimer(WinAnimatorExtended.StickyAnimationTimer, WinAnimatorExtended.StickyAnimationDuration);
    }

    private addStickySymbolsFinished(): void {
        this.emit(WinAnimatorExtended.StickyAnimationFinished);
    }

    private addStickySymbol(reelIndex: number, symbolIndex: number, symbolId: number, init: boolean = false): void {
        if (this.currentStickySymbols[reelIndex][symbolIndex] == null) {
            if (this.currentSymbols[reelIndex][symbolIndex] != null) {
                // symbol on requested position will change its state to STICKY from symbols.xml,
                let symbol = this.currentSymbols[reelIndex][symbolIndex];
                if (symbol != null) {
                    // we ask for symbol with case and ID(always -1 because symbol index  1 in symbols.xml have ID 0 in class symbols.ts
                    let stickySymbol = this.symbols.getSymbol(symbolId - 1, "STICKY_NORMAL"); // symbols property of WinAnimator that we can get & return
                    if (stickySymbol != null) {
                        stickySymbol.x = symbol.x;
                        stickySymbol.y = symbol.y;

                        if (!init) {
                            stickySymbol.goToCase("TO_STICKY");
                            PlayList.play("SND_BaseStick");
                        }

                        // switch positions, Symbols will be = null and SitckySymbols = "Symbol", + move to graphics layer - stickyContainer in freespins.xml
                        this.currentStickySymbols[reelIndex][symbolIndex] = stickySymbol;
                        this.stickyContainer.addChild(stickySymbol);

                        if (BaseView.stickySymbolId === -1) {
                            BaseView.stickySymbolId = stickySymbol.id;
                        }
                        this.fullReels[reelIndex % 5]++;
                        if (this.fullReels[reelIndex % 5] > 3) {
                            this.fullReels[reelIndex % 5] = 3;
                        }
                        this.onSameSymbolsOnReel();
                    }
                }
            }
        }
    }

    // Cleaning memory of stored StickySymbols
    public returnStickySymbols(): void {
        // go trough all reels and symbols on them
        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                // If there is StickySymbol returnSymbol() to remove it and make it null
                if (this.currentStickySymbols[i][j] != null) {
                    this.symbols.returnSymbol(this.currentSymbols[i][j]);
                    this.currentSymbols[i][j] = this.currentStickySymbols[i][j];
                    this.currentStickySymbols[i][j] = null;
                    this.fullReels[i % 5]--;
                    if (this.fullReels[i % 5] < 0) {
                        this.fullReels[i % 5] = 0;
                    }
                }
            }
        }
    }

    public resetStickySymbols(): void {
        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if (this.currentStickySymbols[i][j] != null) {
                    this.currentStickySymbols[i][j].goToCase("STICKY_NORMAL");
                }
            }
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/